/**
 * ADISYS FieldOps — permission catalogue and role matrix.
 *
 * Scope suffixes are meaningful and enforced server-side:
 *   .own   -> only rows the caller owns
 *   .team  -> rows belonging to the caller's direct/indirect reports,
 *             or to projects the caller manages
 *   .all   -> organisation-wide
 *
 * A caller's effective scope for a module is the widest scope they hold.
 */
export const PERMISSIONS = {
    // Dashboard
    'dashboard.view': { module: 'dashboard', description: 'Open the operational dashboard' },
    // Employees
    'employee.view.own': { module: 'employee', description: 'View own employee profile' },
    'employee.view.team': { module: 'employee', description: 'View employees who report to the caller' },
    'employee.view.all': { module: 'employee', description: 'View all employees' },
    'employee.create': { module: 'employee', description: 'Create employees and issue credentials' },
    'employee.update': { module: 'employee', description: 'Edit employee records' },
    'employee.deactivate': { module: 'employee', description: 'Activate or deactivate employees' },
    // Projects
    'project.view.assigned': { module: 'project', description: 'View projects the caller is a member of' },
    'project.view.managed': { module: 'project', description: 'View projects the caller manages' },
    'project.view.all': { module: 'project', description: 'View all projects' },
    'project.create': { module: 'project', description: 'Create projects' },
    'project.update': { module: 'project', description: 'Edit project details' },
    'project.assign_members': { module: 'project', description: 'Add or remove project members' },
    'project.assign_manager': { module: 'project', description: 'Hand a project to a Project Manager' },
    'project.assign_sales_manager': { module: 'project', description: 'Put a Sales Manager in charge of a project' },
    // Work assignments
    'work.view.own': { module: 'work', description: 'View own assignments' },
    'work.view.team': { module: 'work', description: 'View assignments on managed projects / reports' },
    'work.view.all': { module: 'work', description: 'View all assignments' },
    'work.create': { module: 'work', description: 'Assign work to employees' },
    'work.update': { module: 'work', description: 'Edit or reassign work' },
    'work.cancel': { module: 'work', description: 'Cancel an assignment' },
    'work.acknowledge': { module: 'work', description: 'Acknowledge assigned work' },
    'work.progress': { module: 'work', description: 'Update progress and submit work for review' },
    'work.review': { module: 'work', description: 'Accept completion or return for clarification' },
    // Productivity & time
    'time.log': { module: 'productivity', description: 'Record time against assignments' },
    'time.verify': { module: 'productivity', description: 'Verify recorded time as productive hours' },
    'attendance.record': { module: 'productivity', description: 'Check in and out' },
    'productivity.view.own': { module: 'productivity', description: 'View own productivity' },
    'productivity.view.team': { module: 'productivity', description: 'View team productivity' },
    'productivity.view.all': { module: 'productivity', description: 'View organisation-wide productivity' },
    // Expenses
    'expense.create': { module: 'expense', description: 'Submit expense claims' },
    'expense.view.own': { module: 'expense', description: 'View own claims' },
    'expense.view.team': { module: 'expense', description: 'View claims from reports / managed projects' },
    'expense.view.all': { module: 'expense', description: 'View all claims' },
    'expense.approve.manager': { module: 'expense', description: 'Approve, reject or return at the manager stage' },
    'expense.approve.finance': { module: 'expense', description: 'Approve, reject or return at the finance stage' },
    'expense.mark_paid': { module: 'expense', description: 'Record reimbursement payment' },
    'expense.edit_approved': { module: 'expense', description: 'Amend a claim after approval (restricted)' },
    // Leave
    'leave.apply': { module: 'leave', description: 'Apply for leave and withdraw own pending requests' },
    'leave.view.own': { module: 'leave', description: 'View own leave requests' },
    'leave.view.team': { module: 'leave', description: 'View leave for reports / managed projects' },
    'leave.view.all': { module: 'leave', description: 'View all leave requests and the leave calendar' },
    'leave.approve': { module: 'leave', description: 'Approve or reject leave requests' },
    // Reports
    'report.view.own': { module: 'report', description: 'Run reports scoped to self' },
    'report.view.team': { module: 'report', description: 'Run reports scoped to team/projects' },
    'report.view.all': { module: 'report', description: 'Run organisation-wide reports' },
    'report.export': { module: 'report', description: 'Export reports to CSV/PDF' },
    // Administration
    'settings.view': { module: 'settings', description: 'View configuration' },
    'settings.manage': { module: 'settings', description: 'Change configuration' },
    'rbac.manage': { module: 'settings', description: 'Manage roles and permissions' },
    'audit.view': { module: 'settings', description: 'Read the audit log' },
    // CRM — enquiry to delivery. Scope suffixes follow the rule above:
    // .own is the caller's leads and projects, .all is every one.
    'crm.dashboard.view': { module: 'crm', description: 'Open the CRM dashboard for the caller role' },
    'crm.catalog.view': { module: 'crm', description: 'Browse products, categories, brands and stock' },
    'crm.catalog.manage': { module: 'crm', description: 'Create and edit products, categories and brands, and import the catalogue' },
    'crm.vendor.manage': { module: 'crm', description: 'Create and edit supplier vendors' },
    'crm.lead.view.own': { module: 'crm', description: 'View leads assigned to the caller' },
    'crm.lead.view.all': { module: 'crm', description: 'View all leads and customers' },
    'crm.lead.create': { module: 'crm', description: 'Create leads and customer enquiries' },
    'crm.lead.update': { module: 'crm', description: 'Edit leads, log follow-ups and reassign leads' },
    'crm.lead.convert': { module: 'crm', description: 'Convert a qualified lead into a project' },
    'crm.project.view.own': { module: 'crm', description: 'View CRM projects the caller sells or is assigned to' },
    'crm.project.view.all': { module: 'crm', description: 'View every CRM project' },
    'crm.survey.manage': { module: 'crm', description: 'Schedule site surveys, record findings and submit them to the technical team' },
    'crm.boq.manage': { module: 'crm', description: 'Build and revise the Bill of Quantities' },
    'crm.boq.approve': { module: 'crm', description: 'Approve a BOQ internally and submit it for pricing' },
    'crm.pricing.manage': { module: 'crm', description: 'Price a BOQ: costs, charges, discounts and margins' },
    'crm.pricing.approve': { module: 'crm', description: 'Approve pricing and release it to Sales' },
    'crm.cost.view': { module: 'crm', description: 'See confidential cost price and margin figures' },
    'crm.quote.manage': { module: 'crm', description: 'Generate, revise and send quotations and proforma invoices' },
    'crm.approval.record': { module: 'crm', description: 'Record customer approval or rejection of a quotation' },
    'crm.procurement.view': { module: 'crm', description: 'View approved orders, purchase requests, purchase orders and stock' },
    'crm.procurement.manage': { module: 'crm', description: 'Reserve stock, raise purchase requests and orders, receive goods' },
    'crm.finance.view': { module: 'crm', description: 'View invoices, payments and Tally synchronisation' },
    'crm.finance.manage': { module: 'crm', description: 'Issue invoices, record payments, dispatch deliveries and sync Tally' },
    'crm.execution.update': { module: 'crm', description: 'Update installation progress and customer handover' },
    'crm.stage.override': { module: 'crm', description: 'Move a project to any stage and reopen locked documents' },
    'crm.report.view': { module: 'crm', description: 'Open CRM analytics and reports' },
};
export const ALL_PERMISSIONS = Object.keys(PERMISSIONS);
/*
 * A project passes through three pairs of hands:
 *
 *   super_admin    creates it
 *   sales_manager  hands it to a Project Manager
 *   ops_manager    adds technicians and assigns activities
 *
 * `ops_manager` and `employee` keep their keys — renaming them would
 * orphan every existing account, every stored permission override and
 * every audit row — but they are labelled for what they now do.
 */
export const ROLE_KEYS = [
    'super_admin', 'sales_manager', 'ops_manager', 'finance_manager', 'employee',
    // CRM roles
    'sales_executive', 'technical_engineer', 'pricing_manager', 'procurement_manager',
];
export const ROLE_LABELS = {
    super_admin: 'Super Admin',
    sales_manager: 'Sales Manager',
    ops_manager: 'Project Manager',
    finance_manager: 'Finance / Accounts Manager',
    employee: 'Technician / Field Staff',
    sales_executive: 'Sales Executive',
    technical_engineer: 'Technical / Presales Engineer',
    pricing_manager: 'Pricing Manager',
    procurement_manager: 'Procurement Manager',
};
/*
 * A Sales Manager needs to see every project to find the ones still
 * waiting for a manager, and every employee to choose one.
 *
 * They may also be put in charge of a project themselves, so they carry
 * the permissions needed to run one. That is not as wide as it looks:
 * every one of those is scoped per project by `manager_id = caller`, so
 * it only ever applies to projects they have actually been given. It
 * grants nothing on anyone else's.
 */
const SALES_MANAGER = [
    'dashboard.view',
    'employee.view.own', 'employee.view.all',
    'project.view.all', 'project.assign_manager',
    // Running a project they have been made Project Manager of:
    'project.view.managed', 'project.assign_members',
    'work.view.team', 'work.create', 'work.update', 'work.cancel', 'work.review',
    'expense.approve.manager',
    'expense.create', 'expense.view.own',
    'leave.apply', 'leave.view.own',
    'attendance.record',
    'report.view.own',
    'settings.view',
    // CRM: owns enquiries, surveys, quotations and customer approval
    'crm.dashboard.view', 'crm.catalog.view',
    'crm.lead.view.all', 'crm.lead.create', 'crm.lead.update', 'crm.lead.convert',
    'crm.project.view.all', 'crm.survey.manage', 'crm.quote.manage', 'crm.approval.record',
    'crm.report.view',
    // Sales people also do the technical work: build BOQs, update installation. Not boq.approve.
    'crm.boq.manage', 'crm.execution.update',
];
// No 'project.create': a Project Manager runs projects, they no longer open them.
const OPS_MANAGER = [
    'dashboard.view',
    'employee.view.own', 'employee.view.team',
    'project.view.assigned', 'project.view.managed', 'project.update', 'project.assign_members',
    'work.view.own', 'work.view.team', 'work.create', 'work.update', 'work.cancel', 'work.review',
    'work.acknowledge', 'work.progress',
    'time.log', 'time.verify', 'attendance.record',
    'productivity.view.own', 'productivity.view.team',
    'expense.create', 'expense.view.own', 'expense.view.team', 'expense.approve.manager',
    'leave.apply', 'leave.view.own', 'leave.view.team', 'leave.approve',
    'report.view.own', 'report.view.team', 'report.export',
    'settings.view',
    // CRM: the technical side of a Project Manager
    'crm.dashboard.view', 'crm.catalog.view', 'crm.project.view.all',
    'crm.survey.manage', 'crm.boq.manage', 'crm.boq.approve', 'crm.execution.update',
];
const FINANCE_MANAGER = [
    'dashboard.view',
    'employee.view.own', 'employee.view.all',
    'project.view.all',
    'expense.create', 'expense.view.own', 'expense.view.all',
    'expense.approve.finance', 'expense.mark_paid',
    'leave.apply', 'leave.view.own', 'leave.view.all',
    'productivity.view.all',
    'report.view.own', 'report.view.all', 'report.export',
    'settings.view',
    'audit.view',
    // CRM: invoices, payments, delivery and Tally. Deliberately without crm.cost.view.
    'crm.dashboard.view', 'crm.catalog.view', 'crm.project.view.all',
    'crm.procurement.view', 'crm.finance.view', 'crm.finance.manage', 'crm.report.view',
];
const EMPLOYEE = [
    'employee.view.own',
    'project.view.assigned',
    'work.view.own', 'work.acknowledge', 'work.progress',
    'time.log', 'attendance.record',
    'productivity.view.own',
    'expense.create', 'expense.view.own',
    'leave.apply', 'leave.view.own',
    'report.view.own',
];
/*
 * The CRM-only roles share one baseline: they are staff, so they can use the
 * everyday self-service features, but they hold no operational management
 * permission. Everything CRM-specific is listed per role below.
 */
const SALES_EXECUTIVE = [
    'employee.view.own', 'attendance.record',
    'expense.create', 'expense.view.own', 'leave.apply', 'leave.view.own', 'report.view.own',
    'crm.dashboard.view', 'crm.catalog.view',
    'crm.lead.view.own', 'crm.lead.create', 'crm.lead.update', 'crm.lead.convert',
    'crm.project.view.own', 'crm.survey.manage', 'crm.quote.manage', 'crm.approval.record',
    // Sales people also do the technical work: build BOQs, update installation. Not boq.approve.
    'crm.boq.manage', 'crm.execution.update',
];
const TECHNICAL_ENGINEER = [
    'employee.view.own', 'attendance.record',
    'expense.create', 'expense.view.own', 'leave.apply', 'leave.view.own', 'report.view.own',
    'crm.dashboard.view', 'crm.catalog.view',
    'crm.project.view.own', 'crm.survey.manage', 'crm.boq.manage', 'crm.execution.update',
];
// The only non-management role that may see cost price and margin.
const PRICING_MANAGER = [
    'employee.view.own', 'attendance.record',
    'expense.create', 'expense.view.own', 'leave.apply', 'leave.view.own', 'report.view.own',
    'crm.dashboard.view', 'crm.catalog.view', 'crm.project.view.all',
    'crm.pricing.manage', 'crm.pricing.approve', 'crm.cost.view', 'crm.report.view',
];
const PROCUREMENT_MANAGER = [
    'employee.view.own', 'attendance.record',
    'expense.create', 'expense.view.own', 'leave.apply', 'leave.view.own', 'report.view.own',
    'crm.dashboard.view', 'crm.catalog.view', 'crm.catalog.manage', 'crm.vendor.manage',
    'crm.project.view.all', 'crm.procurement.view', 'crm.procurement.manage',
];
export const ROLE_PERMISSIONS = {
    super_admin: ALL_PERMISSIONS,
    sales_manager: SALES_MANAGER,
    ops_manager: OPS_MANAGER,
    finance_manager: FINANCE_MANAGER,
    employee: EMPLOYEE,
    sales_executive: SALES_EXECUTIVE,
    technical_engineer: TECHNICAL_ENGINEER,
    pricing_manager: PRICING_MANAGER,
    procurement_manager: PROCUREMENT_MANAGER,
};
export function resolveScope(held, base) {
    const has = (s) => held.includes(`${base}.${s}`);
    if (has('all'))
        return 'all';
    if (has('team') || has('managed'))
        return 'team';
    if (has('own') || has('assigned'))
        return 'own';
    return 'none';
}
//# sourceMappingURL=permissions.js.map